import { c as _c } from "react/compiler-runtime";
import { BulkChangeCompanyRole } from "@components/table/members/bulk-actions/bulk-change-company-role";
import { BulkSeats } from "@components/table/members/bulk-actions/bulk-seats";
import { BulkExportMembers } from "@components/table/members/bulk-actions/bulk-export-members";
import { BulkRemoveMember } from "@components/table/members/bulk-actions/bulk-remove-member";
import { BulkResendInvitation } from "@components/table/members/bulk-actions/bulk-resend-invitation";
import { BulkSendEmail } from "@components/table/members/bulk-actions/bulk-send-email";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useMemberSeatsLookup } from "@hooks/use-member-seats-lookup";
import { useShouldShowBulkAction } from "@hooks/table/use-should-show-bulk-action";
import { useAppSelector } from "@store/store-helper";
import { fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { useMemo } from "react";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
/** List all the bulk action components for company member table */
export function useCompanyMemberBulkActions(t0) {
  const $ = _c(45);
  const {
    members,
    company
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = selectedEntitiesSelector("members");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const selectedEntities = useAppSelector(t1);
  const {
    isFetchingCompanyMembers
  } = useAppSelector(fetchingMembersFlagsSelector);
  const isAnyRowSelected = selectedEntities.length !== 0;
  let t2;
  if ($[1] !== selectedEntities) {
    t2 = selectedEntities.some(_temp);
    $[1] = selectedEntities;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isAnyMemberPending = t2;
  const {
    canEditMember,
    canDeleteMemberFromCompany,
    canResendInvitationToCompany,
    canManageSceneLicenses,
    canManageXgSeats
  } = useHasUserValidRoleCompanyLevel();
  const {
    isActiveSeatPlan,
    hasSceneSeats
  } = useMemberSeatsLookup();
  const canChangeSeatsInBulk = isActiveSeatPlan && canManageXgSeats || hasSceneSeats && canManageSceneLicenses;
  let allowedButtons;
  if ($[3] !== canChangeSeatsInBulk || $[4] !== canDeleteMemberFromCompany || $[5] !== canEditMember || $[6] !== canResendInvitationToCompany || $[7] !== isAnyMemberPending || $[8] !== isAnyRowSelected) {
    allowedButtons = ["export"];
    if (isAnyRowSelected) {
      allowedButtons.push("sendEmail");
      if (canEditMember) {
        allowedButtons.push("changeRole");
      }
      if (canChangeSeatsInBulk) {
        allowedButtons.push("seats");
      }
      if (isAnyMemberPending && canResendInvitationToCompany) {
        allowedButtons.push("resendInvitation");
      }
      if (canDeleteMemberFromCompany) {
        allowedButtons.push("removeMember");
      }
    }
    $[3] = canChangeSeatsInBulk;
    $[4] = canDeleteMemberFromCompany;
    $[5] = canEditMember;
    $[6] = canResendInvitationToCompany;
    $[7] = isAnyMemberPending;
    $[8] = isAnyRowSelected;
    $[9] = allowedButtons;
  } else {
    allowedButtons = $[9];
  }
  const allowedButtonsBasedOnRowSelection = allowedButtons;
  let t3;
  if ($[10] !== allowedButtonsBasedOnRowSelection) {
    t3 = {
      allowedButtonsBasedOnRowSelection
    };
    $[10] = allowedButtonsBasedOnRowSelection;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const {
    shouldShowBulkButtons
  } = useShouldShowBulkAction(t3);
  let t4;
  if ($[12] !== isFetchingCompanyMembers || $[13] !== members || $[14] !== shouldShowBulkButtons) {
    t4 = shouldShowBulkButtons("export") && (isFetchingCompanyMembers || members.length > 0);
    $[12] = isFetchingCompanyMembers;
    $[13] = members;
    $[14] = shouldShowBulkButtons;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const shouldShowExportButton = t4;
  if (!company) {
    let t5;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        changeRole: null,
        seats: null,
        resendInvitation: null,
        removeMember: null,
        sendEmail: null,
        export: null
      };
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    return t5;
  }
  let t5;
  if ($[17] !== company || $[18] !== shouldShowBulkButtons) {
    t5 = shouldShowBulkButtons("changeRole") ? <BulkChangeCompanyRole companyId={company.id} entityType="members" /> : null;
    $[17] = company;
    $[18] = shouldShowBulkButtons;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== shouldShowBulkButtons) {
    t6 = shouldShowBulkButtons("seats") ? <BulkSeats /> : null;
    $[20] = shouldShowBulkButtons;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== company || $[23] !== selectedEntities || $[24] !== shouldShowBulkButtons) {
    t7 = shouldShowBulkButtons("resendInvitation") ? <BulkResendInvitation selectedEntities={selectedEntities} companyId={company.id} subjectType="workspace" /> : null;
    $[22] = company;
    $[23] = selectedEntities;
    $[24] = shouldShowBulkButtons;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== company || $[27] !== shouldShowBulkButtons) {
    t8 = shouldShowBulkButtons("removeMember") ? <BulkRemoveMember subjectType="workspace" companyId={company.id} /> : null;
    $[26] = company;
    $[27] = shouldShowBulkButtons;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== selectedEntities || $[30] !== shouldShowBulkButtons) {
    t9 = shouldShowBulkButtons("sendEmail") ? <BulkSendEmail selectedEntities={selectedEntities} /> : null;
    $[29] = selectedEntities;
    $[30] = shouldShowBulkButtons;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== company || $[33] !== isAnyRowSelected || $[34] !== members || $[35] !== selectedEntities || $[36] !== shouldShowExportButton) {
    t10 = shouldShowExportButton ? <BulkExportMembers selectedEntities={isAnyRowSelected ? selectedEntities : members} subjectType="workspace" entityName={company.name} areEntitiesSelected={isAnyRowSelected} /> : null;
    $[32] = company;
    $[33] = isAnyRowSelected;
    $[34] = members;
    $[35] = selectedEntities;
    $[36] = shouldShowExportButton;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== t10 || $[39] !== t5 || $[40] !== t6 || $[41] !== t7 || $[42] !== t8 || $[43] !== t9) {
    t11 = {
      changeRole: t5,
      seats: t6,
      resendInvitation: t7,
      removeMember: t8,
      sendEmail: t9,
      export: t10
    };
    $[38] = t10;
    $[39] = t5;
    $[40] = t6;
    $[41] = t7;
    $[42] = t8;
    $[43] = t9;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  return t11;
}
function _temp(member) {
  return !FaroCoreApiUtils.Member.isMemberActive(member);
}